/* eslint-disable consistent-return, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress, Button, IconButton, Tooltip, Snackbar, TextField, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import { describeChannelState } from 'src/sections/@dashboard/payment/paymentLinkApi';
import Iconify from '../Iconify';
import { callApi } from '../../utils/apiCall';
import { resendPaymentLink, sendPaymentLink } from '../../redux/slices/lead/paymentLinkThunks';

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 120; // stop after 10 minutes

const EMAIL_STEPS = [
  { key: 'sent', label: 'Payment link sent via email', icon: 'mdi:email-fast-outline' },
  { key: 'delivered', label: 'Email delivered', icon: 'mdi:email-check-outline' },
  { key: 'opened', label: 'Email opened', icon: 'mdi:email-open-outline' },
  { key: 'paid', label: 'Payment completed', icon: 'mdi:check-decagram' },
];

const WHATSAPP_STEPS = [
  { key: 'sent', label: 'Payment link sent via WhatsApp', icon: 'mdi:whatsapp' },
  { key: 'paid', label: 'Payment completed', icon: 'mdi:check-decagram' },
];

const BOTH_STEPS = [
  { key: 'sent', label: 'Payment link sent via email', icon: 'mdi:email-fast-outline' },
  { key: 'whatsapp_sent', label: 'Payment link sent via WhatsApp', icon: 'mdi:whatsapp' },
  { key: 'delivered', label: 'Email delivered', icon: 'mdi:email-check-outline' },
  { key: 'opened', label: 'Email opened', icon: 'mdi:email-open-outline' },
  { key: 'paid', label: 'Payment completed', icon: 'mdi:check-decagram' },
];

const getTrackingSteps = (channel) => {
  if (channel === 'whatsapp') return WHATSAPP_STEPS;
  if (channel === 'both') return BOTH_STEPS;
  return EMAIL_STEPS;
};

/**
 * Real-time payment link tracker — polls checkout session for payment status
 * and shows contact info + WhatsApp send option.
 */
export default function PaymentLinkTracker({
  practiceId, leadId, treatmentId, paymentId,
  contactEmail, contactPhone, checkoutUrl, linkChannel,
  onPaymentStatusChange, externalPaid,
  onMarkAsPaid, markingPaid,
}) {
  const [paymentStatus, setPaymentStatus] = useState('unpaid'); // 'unpaid' | 'paid'
  const [emailEvents, setEmailEvents] = useState([]); // e.g. ['delivered', 'opened']
  const [whatsappDelivery, setWhatsappDelivery] = useState(null); // latest {state, error} from session.deliveries (contract E)
  const [polling, setPolling] = useState(true);
  const [copied, setCopied] = useState(false);
  const [pollError, setPollError] = useState(null);
  // Resend / edit email state
  const [editingEmail, setEditingEmail] = useState(false);
  const [editEmail, setEditEmail] = useState(contactEmail || '');
  const [resendingChannel, setResendingChannel] = useState(null); // 'email' | 'whatsapp' | 'both' | null
  const [resendResult, setResendResult] = useState(null); // 'success' | 'error'
  const [resendError, setResendError] = useState(null);
  const [linkExpired, setLinkExpired] = useState(false);
  const [replacingLink, setReplacingLink] = useState(false);
  const pollCount = useRef(0);
  const consecutiveErrors = useRef(0);
  const mountedRef = useRef(true);
  const intervalRef = useRef(null);

  const checkStatus = useCallback(async () => {
    if (!practiceId || !leadId || !treatmentId || !paymentId) return;
    if (!mountedRef.current) return;

    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${paymentId}/checkout_sessions/`
      );
      if (!mountedRef.current) return;

      if (res.ok && Array.isArray(res.data?.results) && res.data.results.length > 0) {
        consecutiveErrors.current = 0;
        const session = res.data.results[0];

        // Parse Mailgun email events from the API response
        if (Array.isArray(session.email_events) && session.email_events.length > 0) {
          setEmailEvents(session.email_events.map((e) => e.event || e));
        }

        // Contract E — session.deliveries: [{id, channel, state, error, recipient_masked,
        // created_at, created_by_name}] newest first. Surface the latest WhatsApp one.
        if (Array.isArray(session.deliveries) && session.deliveries.length > 0) {
          const latestWhatsapp = session.deliveries.find((d) => d.channel === 'whatsapp' || d.channel === 'WHATSAPP');
          if (latestWhatsapp) {
            setWhatsappDelivery({ state: (latestWhatsapp.state || '').toLowerCase(), error: latestWhatsapp.error || null, code: latestWhatsapp.code || null });
          }
        }
        if (session.is_expired) {
          setLinkExpired(true);
        }

        if (session.payment_status === 'paid') {
          setPaymentStatus('paid');
          setPolling(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          if (onPaymentStatusChange) onPaymentStatusChange('paid');
          return;
        }
      } else if (!res.ok) {
        // HTTP error (404, 500, etc.) — callApi doesn't throw on these
        consecutiveErrors.current += 1;
        if (consecutiveErrors.current >= 3) {
          setPollError('Unable to check payment status. Please check the payment tab for updates.');
          setPolling(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('[PaymentLinkTracker] Poll failed', err);
      consecutiveErrors.current += 1;
      if (consecutiveErrors.current >= 3) {
        setPollError('Unable to check payment status. Please check the payment tab for updates.');
        setPolling(false);
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }
    }

    pollCount.current += 1;
    if (pollCount.current >= MAX_POLLS) {
      setPolling(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [practiceId, leadId, treatmentId, paymentId]);

  // React to parent marking payment as paid (e.g. "Mark as Paid" button in footer)
  useEffect(() => {
    if (externalPaid && paymentStatus !== 'paid') {
      setPaymentStatus('paid');
      setPolling(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [externalPaid]);

  useEffect(() => {
    mountedRef.current = true;
    if (!practiceId || !leadId || !treatmentId || !paymentId) {
      setPolling(false);
      return;
    }

    const timeout = setTimeout(() => {
      checkStatus();
      intervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);
    }, 2000);

    return () => {
      mountedRef.current = false;
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [practiceId, leadId, treatmentId, paymentId, checkStatus]);

  // ── Step state logic ───────────────────────────────────────────────
  const channel = linkChannel || 'email';
  const trackingSteps = getTrackingSteps(channel);

  const getStepState = (stepKey) => {
    if (stepKey === 'sent') return 'done';
    if (stepKey === 'whatsapp_sent') return 'done';
    if (stepKey === 'delivered') {
      if (emailEvents.includes('delivered') || paymentStatus === 'paid') return 'done';
      return pollCount.current >= 1 ? 'active' : 'pending';
    }
    if (stepKey === 'opened') {
      if (emailEvents.includes('opened') || emailEvents.includes('clicked') || paymentStatus === 'paid') return 'done';
      if (emailEvents.includes('delivered')) return 'active';
      return 'pending';
    }
    if (stepKey === 'paid') {
      if (paymentStatus === 'paid') return 'done';
      // For WhatsApp-only, mark active immediately (no email delivery to wait for)
      if (channel === 'whatsapp') return 'active';
      if (emailEvents.includes('delivered')) return 'active';
      return 'pending';
    }
    return 'pending';
  };

  const getStepColor = (state) => {
    if (state === 'done') return '#00AB55';
    if (state === 'active') return '#FFAB00';
    return 'rgba(145, 158, 171, 0.32)';
  };

  const getStepIcon = (step, state) => {
    if (state === 'done') return 'mdi:check-circle';
    return step.icon;
  };

  const isPaid = paymentStatus === 'paid';
  const headerIcon = channel === 'whatsapp' ? 'mdi:whatsapp' : 'mdi:email-sync-outline';

  // ── Actions ────────────────────────────────────────────────────────
  const handleCopyLink = () => {
    if (!checkoutUrl) return;
    if (!navigator.clipboard?.writeText) {
      console.warn('[PaymentLinkTracker] Clipboard API not available');
      return;
    }
    navigator.clipboard.writeText(checkoutUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      console.warn('[PaymentLinkTracker] Clipboard write failed', err);
    });
  };

  // Resend via the command (contract B) — replaces the raw checkout_sessions POST
  // and the separate lead_contact PATCH; the server persists the (possibly edited)
  // email/phone as part of the send. Also replaces the old wa.me popup for WhatsApp.
  const handleResend = async (channels) => {
    if (!practiceId || !leadId || !treatmentId || !paymentId) return;
    setResendingChannel(channels.length > 1 ? 'both' : channels[0]);
    setResendResult(null);
    setResendError(null);

    const emailChanged = editEmail.trim() && editEmail.trim() !== contactEmail;
    const body = {
      channels,
      ...(channels.includes('email') && emailChanged ? { email: editEmail.trim() } : {}),
      ...(channels.includes('whatsapp') && contactPhone ? { phone: contactPhone } : {}),
    };

    const result = await resendPaymentLink({ practiceId, leadId, treatmentId, paymentId, body });

    if (result.status === 409) {
      setLinkExpired(true);
      setResendResult('error');
      setResendError(result.error || 'This payment link has expired.');
    } else if (result.ok) {
      setResendResult('success');
      setResendError(null);
      setLinkExpired(false);
      setEditingEmail(false);
      if (channels.includes('whatsapp') && result.data?.channels?.whatsapp) {
        const wa = result.data.channels.whatsapp;
        setWhatsappDelivery({ state: (wa.state || '').toLowerCase(), error: wa.error || null, code: wa.code || null });
      }
    } else {
      setResendResult('error');
      setResendError(result.error || 'Failed to resend payment link. Try copying the link manually.');
    }
    setResendingChannel(null);
  };

  const handleResendEmail = () => handleResend(['email']);
  const handleSendWhatsApp = () => handleResend(['whatsapp']);
  const handleResendBoth = () => handleResend(['email', 'whatsapp']);

  // A 409 link_expired means the checkout session itself expired — resend
  // (contract B) reuses an OPEN session, so it can't recover from this.
  // sendPaymentLink with payment_id + replace_expired:true (contract A)
  // creates a fresh checkout session for the same payment.
  const handleReplaceLink = async () => {
    if (!practiceId || !leadId || !treatmentId || !paymentId) return;
    const channels = channel === 'both' ? ['email', 'whatsapp'] : [channel];
    setReplacingLink(true);
    setResendResult(null);
    setResendError(null);

    const emailChanged = editEmail.trim() && editEmail.trim() !== contactEmail;
    const body = {
      payment_id: paymentId,
      replace_expired: true,
      channels,
      ...(channels.includes('email') && emailChanged ? { email: editEmail.trim() } : {}),
      ...(channels.includes('whatsapp') && contactPhone ? { phone: contactPhone } : {}),
    };

    const result = await sendPaymentLink({ practiceId, leadId, treatmentId, body });

    if (result.ok) {
      setLinkExpired(false);
      setResendResult('success');
      setResendError(null);
      setEditingEmail(false);
      if (channels.includes('whatsapp') && result.data?.channels?.whatsapp) {
        const wa = result.data.channels.whatsapp;
        setWhatsappDelivery({ state: (wa.state || '').toLowerCase(), error: wa.error || null, code: wa.code || null });
      }
    } else {
      setResendResult('error');
      setResendError(result.error || 'Failed to replace the expired payment link.');
    }
    setReplacingLink(false);
  };

  return (
    <Box sx={{
      mt: 2,
      p: 2,
      borderRadius: 2,
      bgcolor: isPaid ? alpha('#00AB55', 0.06) : alpha('#FFAB00', 0.06),
      border: '1px solid',
      borderColor: isPaid ? alpha('#00AB55', 0.16) : alpha('#FFAB00', 0.16),
      transition: 'all 0.3s ease',
      textAlign: 'left',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Iconify
          icon={isPaid ? 'mdi:check-decagram' : headerIcon}
          width={18}
          sx={{ color: isPaid ? '#00AB55' : '#FFAB00', mr: 1 }}
        />
        <Typography variant="subtitle2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
          {isPaid ? 'Payment received' : 'Payment link tracking'}
        </Typography>
        {polling && !isPaid && (
          <CircularProgress size={14} sx={{ ml: 'auto', color: '#FFAB00' }} />
        )}
        {isPaid && (
          <Iconify icon="mdi:check-bold" width={16} sx={{ ml: 'auto', color: '#00AB55' }} />
        )}
      </Box>

      {/* Contact info bar — email is editable when not paid */}
      {(contactEmail || contactPhone) && (
        <Box sx={{
          mb: 1.5,
          p: 1.5,
          borderRadius: '10px',
          bgcolor: 'rgba(255,255,255,0.6)',
          border: '1px solid rgba(145, 158, 171, 0.12)',
        }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            {contactEmail && !editingEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Iconify icon="mdi:email-outline" width={15} sx={{ color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {editEmail.trim() && editEmail.trim() !== contactEmail ? editEmail.trim() : contactEmail}
                </Typography>
                {!isPaid && (
                  <Tooltip title="Edit email address">
                    <IconButton size="small" onClick={() => setEditingEmail(true)} sx={{ p: 0.25, ml: 0.25 }}>
                      <Iconify icon="mdi:pencil-outline" width={14} sx={{ color: 'text.disabled' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            {editingEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                <TextField
                  size="small"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="Patient email"
                  InputProps={{
                    startAdornment: <Iconify icon="mdi:email-outline" width={15} sx={{ color: 'text.disabled', mr: 0.5 }} />,
                    sx: { fontSize: '0.75rem', height: 32 },
                  }}
                  sx={{ flex: 1, minWidth: 160 }}
                />
                <Tooltip title="Save">
                  <IconButton size="small" onClick={() => setEditingEmail(false)} sx={{ p: 0.25 }}>
                    <Iconify icon="mdi:check" width={16} sx={{ color: '#00AB55' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton size="small" onClick={() => { setEditEmail(contactEmail || ''); setEditingEmail(false); }} sx={{ p: 0.25 }}>
                    <Iconify icon="mdi:close" width={16} sx={{ color: 'text.disabled' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {contactPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Iconify icon="mdi:phone-outline" width={15} sx={{ color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {contactPhone}
                </Typography>
              </Box>
            )}
          </Box>
          {editEmail.trim() && editEmail.trim() !== contactEmail && !editingEmail && (
            <Typography variant="caption" sx={{ color: '#078DEE', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
              Email updated — click Resend to send to the new address
            </Typography>
          )}
        </Box>
      )}

      {/* Step indicators */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 0.5 }}>
        {trackingSteps.map((step, idx) => {
          const state = getStepState(step.key);
          const color = getStepColor(state);
          return (
            <Box key={step.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ width: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {idx > 0 && (
                  <Box sx={{
                    width: '2px',
                    height: '6px',
                    bgcolor: getStepState(trackingSteps[idx - 1].key) === 'done' ? '#00AB55' : 'rgba(145, 158, 171, 0.24)',
                    mb: '2px',
                    borderRadius: 1,
                    transition: 'background-color 0.3s ease',
                  }} />
                )}
                <Iconify
                  icon={getStepIcon(step, state)}
                  width={18}
                  sx={{
                    color,
                    transition: 'color 0.3s ease',
                    ...(state === 'active' ? {
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                      },
                    } : {}),
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8125rem',
                  color: state === 'pending' ? 'text.disabled' : 'text.primary',
                  fontWeight: state === 'active' ? 600 : 400,
                  transition: 'all 0.3s ease',
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* WhatsApp delivery state (contract E session.deliveries, or the latest
          command/resend response — either can carry a `blocked` state with
          code `not_sent_other_channel_blocked`, which is not a real failure). */}
      {(channel === 'whatsapp' || channel === 'both') && whatsappDelivery && (() => {
        const { label, isOtherChannelBlock } = describeChannelState(whatsappDelivery);
        const isFailure = !isOtherChannelBlock && ['failed', 'blocked'].includes(whatsappDelivery.state);
        let waIcon = 'mdi:whatsapp';
        let waColor = '#25D366';
        if (isFailure) {
            waIcon = 'mdi:alert-circle-outline';
            waColor = '#FF5630';
        } else if (isOtherChannelBlock) {
            waIcon = 'mdi:minus-circle-outline';
            waColor = '#919EAB';
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, px: 0.5 }}>
            <Iconify
              icon={waIcon}
              width={15}
              sx={{ color: waColor }}
            />
            <Typography variant="caption" sx={{ color: isFailure ? '#FF5630' : 'text.secondary', fontSize: '0.75rem' }}>
              {isOtherChannelBlock ? label : `WhatsApp ${label}${whatsappDelivery.error ? ` — ${whatsappDelivery.error}` : ''}`}
            </Typography>
          </Box>
        );
      })()}

      {linkExpired && (
        <Alert
          severity="warning"
          sx={{ mt: 1.5, py: 0.25, fontSize: '0.75rem' }}
          action={(
            <Button
              size="small"
              color="warning"
              onClick={handleReplaceLink}
              disabled={replacingLink}
              startIcon={replacingLink ? <CircularProgress size={12} /> : <Iconify icon="mdi:autorenew" width={14} />}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {replacingLink ? 'Replacing…' : 'Replace link'}
            </Button>
          )}
        >
          This payment link has expired. Replace it to create a fresh one.
        </Alert>
      )}

      {/* Resend feedback */}
      {resendResult === 'success' && (
        <Alert severity="success" sx={{ mt: 1.5, py: 0.25, fontSize: '0.75rem' }} onClose={() => setResendResult(null)}>
          Payment link resent successfully
        </Alert>
      )}
      {resendResult === 'error' && resendError && (
        <Alert severity="warning" sx={{ mt: 1.5, py: 0.25, fontSize: '0.75rem' }} onClose={() => { setResendResult(null); setResendError(null); }}>
          {resendError}
        </Alert>
      )}

      {/* Actions: Resend Email, WhatsApp, Copy link */}
      {!isPaid && (
        <Box sx={{
          display: 'flex',
          gap: 1,
          mt: 2,
          pt: 1.5,
          borderTop: '1px solid rgba(145, 158, 171, 0.12)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          {/* Resend email — visible when a contact email is on file */}
          {contactEmail && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleResendEmail}
              disabled={!!resendingChannel}
              startIcon={resendingChannel === 'email'
                ? <CircularProgress size={14} />
                : <Iconify icon="mdi:email-arrow-right-outline" width={16} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                borderColor: alpha('#078DEE', 0.4),
                color: '#078DEE',
                py: 0.25,
                px: 1.5,
                borderRadius: '20px',
                '&:hover': {
                  bgcolor: alpha('#078DEE', 0.08),
                  borderColor: '#078DEE',
                },
              }}
            >
              {resendingChannel === 'email' ? 'Sending…' : 'Resend Email'}
            </Button>
          )}
          {/* Resend WhatsApp — visible when a contact phone is on file. Goes through
              the command (contract B) rather than a client-side wa.me popup. */}
          {contactPhone && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleSendWhatsApp}
              disabled={!!resendingChannel}
              startIcon={resendingChannel === 'whatsapp'
                ? <CircularProgress size={14} />
                : <Iconify icon="mdi:whatsapp" width={16} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                borderColor: '#25D366',
                color: '#25D366',
                py: 0.25,
                px: 1.5,
                borderRadius: '20px',
                '&:hover': {
                  bgcolor: alpha('#25D366', 0.08),
                  borderColor: '#25D366',
                },
              }}
            >
              {resendingChannel === 'whatsapp' ? 'Sending…' : 'Resend WhatsApp'}
            </Button>
          )}
          {/* Combined resend — only worth offering when both channels are usable */}
          {contactEmail && contactPhone && (
            <Button
              size="small"
              variant="text"
              onClick={handleResendBoth}
              disabled={!!resendingChannel}
              startIcon={resendingChannel === 'both'
                ? <CircularProgress size={14} />
                : <Iconify icon="mdi:send-check-outline" width={16} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'text.secondary',
                py: 0.25,
                px: 1,
              }}
            >
              {resendingChannel === 'both' ? 'Sending…' : 'Both'}
            </Button>
          )}
          {checkoutUrl && (
            <Tooltip title={copied ? 'Copied!' : 'Copy payment link'}>
              <IconButton
                size="small"
                onClick={handleCopyLink}
                sx={{ border: '1px solid rgba(145, 158, 171, 0.24)', borderRadius: '20px', p: 0.5 }}
              >
                <Iconify icon={copied ? 'mdi:check' : 'mdi:content-copy'} width={16} sx={{ color: copied ? '#00AB55' : 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          )}
          {onMarkAsPaid && (
            <LoadingButton
              size="small"
              variant="outlined"
              loading={markingPaid}
              onClick={onMarkAsPaid}
              startIcon={<Iconify icon="mdi:check-circle-outline" width={16} />}
              sx={{
                ml: 'auto',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                borderColor: alpha('#00AB55', 0.4),
                color: '#00AB55',
                py: 0.25,
                px: 1.5,
                borderRadius: '20px',
                '&:hover': {
                  bgcolor: alpha('#00AB55', 0.08),
                  borderColor: '#00AB55',
                },
              }}
            >
              Mark as Paid
            </LoadingButton>
          )}
        </Box>
      )}

      {/* Timeout / error notice */}
      {!polling && !isPaid && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          {pollError || 'Live tracking timed out — check the lead\u0027s payment tab for the latest status.'}
        </Typography>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Payment link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

PaymentLinkTracker.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  treatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  paymentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  contactEmail: PropTypes.string,
  contactPhone: PropTypes.string,
  checkoutUrl: PropTypes.string,
  linkChannel: PropTypes.oneOf(['email', 'whatsapp', 'both']),
  onPaymentStatusChange: PropTypes.func,
  externalPaid: PropTypes.bool,
  onMarkAsPaid: PropTypes.func,
  markingPaid: PropTypes.bool,
};

PaymentLinkTracker.defaultProps = {
  contactEmail: null,
  contactPhone: null,
  checkoutUrl: null,
  linkChannel: 'email',
  onPaymentStatusChange: null,
  externalPaid: false,
  onMarkAsPaid: null,
  markingPaid: false,
};
